import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, ShieldCheck } from 'lucide-react';
import { Card } from '../components/ui/card';

const UPDATED = 'June 5, 2026';

const CONTENT = {
  privacy: {
    icon: ShieldCheck,
    title: 'Privacy Policy',
    intro: 'This Privacy Policy explains how Health Buddy collects, uses, stores, and shares information when patients, doctors, and caregivers use the app, WhatsApp bot, reports, and Health Hub tools.',
    sections: [
      ['Information we collect', [
        'Account details such as name, phone number, email, age, gender, language, and login method.',
        'Health information you choose to share, including symptoms, checkup answers, vitals, medicines, allergies, reports, records, wearable metrics, emergency contacts, and doctor consultation notes.',
        'Usage and security data such as login events, request logs, device/browser details, audit logs, and error information needed to protect the service.',
      ]],
      ['How we use information', [
        'To create symptom summaries, checkup profiles, Health Hub charts, vault PDFs, doctor handoff reports, and consultation records.',
        'To authenticate users, protect accounts, prevent abuse, troubleshoot errors, and maintain service reliability.',
        'To send notifications or report links only when enabled or requested by the user.',
      ]],
      ['AI and medical safety', [
        'Health Buddy is an AI intake and health profile assistant. It is not a doctor and does not provide final diagnosis, prescription, or emergency care.',
        'AI-generated summaries are intended to help a qualified doctor review patient information faster.',
        'In an emergency, users should contact local emergency services or visit the nearest hospital immediately.',
      ]],
      ['Sharing and access', [
        'Reports may be shared with doctors selected by the patient or through secure report links generated by the app.',
        'Doctors can access only consultations and reports assigned to them.',
        'Public report links and wearable sync tokens should be treated as sensitive and shared carefully.',
      ]],
      ['Data protection', [
        'The app uses authentication, rate limits, security headers, hashed OTP storage, audit logs, and access controls to reduce risk.',
        'No system can be guaranteed fully secure. Users should protect their phone, email, OTPs, and report links.',
        'Production deployments should use strong secrets, HTTPS, strict CORS, secure hosting, and regular access review.',
      ]],
      ['Retention and deletion', [
        'Health records are stored while the account or service remains active, unless deletion is requested or required by law.',
        'Users may request export, correction, or deletion of their records by contacting support.',
      ]],
      ['Contact', [
        'For privacy questions, correction requests, or deletion requests, contact care@healthbuddy.ai.',
      ]],
    ],
  },
  terms: {
    icon: FileText,
    title: 'Terms of Service',
    intro: 'These Terms govern use of Health Buddy, including the web app, WhatsApp bot, AI summaries, Health Hub, vault reports, and doctor consultation features.',
    sections: [
      ['Use of the service', [
        'You agree to provide accurate information and use the service only for lawful health support, record keeping, and consultation preparation.',
        'You are responsible for protecting your login OTPs, account access, report links, and wearable sync tokens.',
      ]],
      ['Not medical advice', [
        'Health Buddy is not a substitute for a licensed doctor, hospital, emergency service, or professional medical judgment.',
        'AI outputs may be incomplete or incorrect and must be reviewed by a qualified healthcare professional before medical decisions are made.',
        'Do not delay emergency care because of information shown by Health Buddy.',
      ]],
      ['Doctor consultations', [
        'Doctor profiles, responses, and availability depend on each doctor and local verification processes.',
        'Doctors are responsible for their own clinical advice, prescriptions, follow-ups, and professional obligations.',
      ]],
      ['Reports and health records', [
        'Reports are intake summaries and may include patient-provided information, AI-generated structure, and Health Hub data.',
        'Users should review summaries before sharing them and correct any inaccurate information.',
      ]],
      ['Prohibited use', [
        'Do not misuse the service, attempt unauthorized access, scrape data, upload harmful content, impersonate others, or interfere with the platform.',
        'Do not use Health Buddy to issue fake medical documents, prescriptions, or emergency claims.',
      ]],
      ['Limitation of liability', [
        'The service is provided for informational and workflow support. To the maximum extent allowed by law, Health Buddy is not liable for medical decisions, delays in care, third-party services, or user-provided inaccurate information.',
      ]],
      ['Changes', [
        'These Terms may be updated as the service improves. Continued use after updates means you accept the revised Terms.',
      ]],
      ['Contact', [
        'For questions about these Terms, contact care@healthbuddy.ai.',
      ]],
    ],
  },
};

export default function LegalPage({ type = 'privacy' }) {
  const page = CONTENT[type] || CONTENT.privacy;
  const Icon = page.icon;
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-2xl bg-[#1E3F33] text-white grid place-items-center shrink-0">
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <h1 className="font-display text-4xl sm:text-5xl text-[#1A1A1A] leading-tight">{page.title}</h1>
          <p className="text-sm text-[#7A7A7A] mt-2">Last updated: {UPDATED}</p>
        </div>
      </div>

      <Card className="mt-8 p-5 sm:p-7 rounded-2xl border border-[#E8E1D5] bg-white hb-shadow-soft">
        <p className="text-[16px] leading-relaxed text-[#4A4A4A]">{page.intro}</p>
        <div className="mt-7 space-y-7">
          {page.sections.map(([title, items]) => (
            <section key={title}>
              <h2 className="font-semibold text-xl text-[#1E3F33]">{title}</h2>
              <ul className="mt-3 space-y-2 list-disc pl-5 text-[15px] leading-relaxed text-[#1A1A1A]">
                {items.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </section>
          ))}
        </div>
      </Card>

      <div className="mt-6 flex flex-wrap gap-3 text-sm font-semibold">
        <Link to="/privacy" className="text-[#1E3F33] hover:underline">Privacy Policy</Link>
        <Link to="/terms" className="text-[#1E3F33] hover:underline">Terms of Service</Link>
        <Link to="/" className="text-[#1E3F33] hover:underline">Back to Home</Link>
      </div>
    </div>
  );
}
